import SiteFooter from '@/components/site-footer';
import SiteHeader from '@/components/site-header';
import { buttonVariants } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  buscarSessao,
  definirSemSessao,
  limparSessao,
  redirecionarParaLogin,
  redirecionarParaLogout,
} from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import * as z from 'zod';
import { AcessoNegado } from './-acesso-negado';
import { FalhaLogin } from './-falha-login';

/**
 * Rota de entrada do login. Ela cobre três momentos:
 *
 * - entrada sem sessão: manda para a tela de login do Autenticador;
 * - entrada com sessão: manda para o dashboard, e de lá o guard despacha;
 * - falha (`erro`): exibe a mensagem, sem redirecionar.
 *
 * O sucesso do login não passa por aqui: o callback da API manda o navegador direto
 * para o destino final.
 */
export const Route = createFileRoute('/__public/login/')({
  component: RouteComponent,
  validateSearch: z.object({
    from: z.literal('v1').optional(),
    reason: z.string().optional(),
    redirect: z.string().optional(),
    erro: z.enum(['falha-login', 'acesso-negado']).optional(),
  }),
  beforeLoad: async ({ search }) => {
    // O logoff do site antigo não encerra a sessão do Autenticador, então passamos
    // pelo logout da API, que encerra as duas.
    if (search.from === 'v1' && search.reason === 'logoff') {
      limparSessao();

      return redirecionarParaLogout({ replace: true });
    }

    // A API acabou de dizer que o login não deu certo, então não há sessão para
    // procurar. Registrar isso evita que o cabeçalho pergunte por conta própria.
    if (search.erro) {
      definirSemSessao();

      return;
    }

    const usuario = await buscarSessao();

    if (!usuario) {
      return redirecionarParaLogin({
        replace: true,
        redirect: search.redirect,
      });
    }

    // Quem não é revenda (o aluno) é despachado pelo guard das rotas autenticadas,
    // que é por onde todo mundo passa depois do login.
    throw redirect({ to: '/dashboard', replace: true });
  },
});

const linkClassName = buttonVariants({
  className: 'mt-8 bg-zinc-600 px-12 py-5 text-sm uppercase hover:no-underline',
});

function RouteComponent() {
  const { t } = useTranslation('public');

  const { erro } = Route.useSearch();

  return (
    <>
      <SiteHeader />
      <div>
        <section
          className={cn(
            'flex h-[clamp(10rem,25vh,18rem)] flex-col items-center justify-center gap-4 text-white',
            'bg-[url(/banner_login.jpg)] bg-cover bg-center bg-no-repeat',
          )}
        >
          <h1 className="max-w-sm text-center text-[2.5rem] leading-10 font-semibold">
            {t($ => $.login.title)}
          </h1>
          <p className="max-w-lg text-center text-[1.05rem]">
            {t($ => $.login.subtitle)}
          </p>
        </section>
        <main
          className={cn(
            'flex flex-col items-center *:py-6',
            'md:flex-row md:items-start md:justify-center md:divide-x md:*:w-md md:*:px-10',
          )}
        >
          {erro === 'acesso-negado' ? <AcessoNegado /> : <FalhaLogin />}

          <Separator
            orientation="horizontal"
            className="w-2/3! bg-secullum-yellow py-0! md:hidden"
          />

          <section className="flex w-xs flex-col sm:w-sm lg:justify-end">
            <h2 className="text-2xl text-gray-600">
              {t($ => $.notAResellerQuestion)}
            </h2>
            <p className="mt-1 max-w-64 text-gray-400">
              {t($ => $.registerNow)}
            </p>
            <Link to="/cadastro-revendedor" className={linkClassName}>
              {t($ => $.becomeAReseller)}
            </Link>
          </section>
        </main>
      </div>
      <SiteFooter />
    </>
  );
}
